import { box, qs, enc, toPage, type Http, type Page } from './core';

export interface Recipient {
  emailAddress: { address: string; name?: string };
}
export interface MailSummary {
  id: string;
  conversationId: string;
  subject: string | null;
  bodyPreview: string;
  from?: Recipient;
  toRecipients: Recipient[];
  receivedDateTime: string;
  isRead: boolean;
  hasAttachments: boolean;
  importance: 'low' | 'normal' | 'high';
  flag?: { flagStatus: 'notFlagged' | 'flagged' | 'complete' };
  parentFolderId?: string;
}
export interface MailMessage extends MailSummary {
  body: { contentType: 'html' | 'text'; content: string };
  ccRecipients: Recipient[];
  internetMessageId?: string;
}
export interface Attachment {
  id: string;
  name: string;
  contentType: string;
  size: number;
  isInline: boolean;
}
export interface DraftInput {
  subject: string;
  body: string;
  html?: boolean;
  to: string[];
  cc?: string[];
}

const SUMMARY = 'id,conversationId,subject,bodyPreview,from,toRecipients,receivedDateTime,isRead,hasAttachments,importance,flag,parentFolderId';
const rcpt = (a: string[] = []): Recipient[] => a.map((address) => ({ emailAddress: { address } }));
const draftBody = (d: DraftInput) => ({ subject: d.subject, body: { contentType: d.html ? 'html' : 'text', content: d.body }, toRecipients: rcpt(d.to), ccRecipients: rcpt(d.cc) });

export function mailApi(h: Http) {
  return {
    /** Danh sách thư (mới trước). `folder`: inbox · sentitems · drafts · id thư mục. `next` = trang kế. */
    async list(o: { mailbox?: string; folder?: string; top?: number; unread?: boolean; search?: string; next?: string } = {}): Promise<Page<MailSummary>> {
      if (o.next) return toPage(await h.json('mailRead', 'GET', o.next));
      const q = new URLSearchParams({ $select: SUMMARY, $top: String(Math.min(o.top ?? 25, 100)) });
      if (o.search) q.set('$search', `"${o.search.replace(/"/g, '')}"`);
      else q.set('$orderby', 'receivedDateTime desc');
      if (o.unread && !o.search) q.set('$filter', 'isRead eq false');
      return toPage(await h.json('mailRead', 'GET', `${box(o.mailbox)}/mailFolders/${enc(o.folder ?? 'inbox')}/messages?${qs(q)}`));
    },
    get: (id: string, mailbox?: string) => h.json<MailMessage>('mailRead', 'GET', `${box(mailbox)}/messages/${enc(id)}`),
    /** Cả luồng thư (theo conversationId), cũ → mới. */
    async thread(conversationId: string, mailbox?: string): Promise<MailMessage[]> {
      const q = new URLSearchParams({ $filter: `conversationId eq '${conversationId.replace(/'/g, "''")}'`, $top: '50' });
      const r = await h.json<{ value: MailMessage[] }>('mailRead', 'GET', `${box(mailbox)}/messages?${qs(q)}`);
      return [...r.value].sort((a, b) => a.receivedDateTime.localeCompare(b.receivedDateTime));
    },
    async attachments(id: string, mailbox?: string): Promise<Attachment[]> {
      const q = '$select=id,name,contentType,size,isInline';
      return (await h.json<{ value: Attachment[] }>('mailRead', 'GET', `${box(mailbox)}/messages/${enc(id)}/attachments?${q}`)).value;
    },
    async markRead(id: string, isRead = true, mailbox?: string) {
      await h.json('mailWrite', 'PATCH', `${box(mailbox)}/messages/${enc(id)}`, { isRead });
    },
    async flag(id: string, flagStatus: 'flagged' | 'notFlagged' | 'complete', mailbox?: string) {
      await h.json('mailWrite', 'PATCH', `${box(mailbox)}/messages/${enc(id)}`, { flag: { flagStatus } });
    },
    /** Chuyển thư mục (inbox · archive · deleteditems · id). Trả id mới của thư. */
    async move(id: string, destination: string, mailbox?: string): Promise<string> {
      return (await h.json<{ id: string }>('mailWrite', 'POST', `${box(mailbox)}/messages/${enc(id)}/move`, { destinationId: destination })).id;
    },
    createDraft: (d: DraftInput, mailbox?: string) => h.json<MailMessage>('mailWrite', 'POST', `${box(mailbox)}/messages`, draftBody(d)),
    /** Nháp trả lời / trả lời tất cả; `comment` đặt đầu thân thư. */
    createReply: (id: string, o: { all?: boolean; comment?: string } = {}, mailbox?: string) =>
      h.json<MailMessage>('mailWrite', 'POST', `${box(mailbox)}/messages/${enc(id)}/${o.all ? 'createReplyAll' : 'createReply'}`, { comment: o.comment ?? '' }),
    async sendDraft(id: string, mailbox?: string) {
      await h.json(['mailSend', 'mailWrite'], 'POST', `${box(mailbox)}/messages/${enc(id)}/send`);
    },
    /** Gửi thẳng (không lưu nháp). Hộp thư chung: gửi từ `mailbox`. */
    async send(d: DraftInput, mailbox?: string) {
      await h.json('mailSend', 'POST', `${box(mailbox)}/sendMail`, { message: draftBody(d), saveToSentItems: true });
    },
  };
}
